import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { callFunction } from "../../api/callFunction";
import { doc, writeBatch } from "firebase/firestore";
import { db } from "../../firebase";
import Api from "../../api/api";
import { Input } from "../../components/Input";
import { showToast } from "../../utils/showToast";
import { GlobalContext } from "../../context/GlobalContext";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

export default function HostForm() {
  const { vendorID } = useParams();
  const navigate = useNavigate();
  const { globalState } = useContext(GlobalContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingVendor, setDeletingVendor] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [purging, setPurging] = useState(false);
  const [vendor, setVendor] = useState({
    name: "",
    vendorID: "",
    photoURL: "",
    meetupMemberID: "",
    bio: "",
    isSuperAdmin: false,
  });
  const [user, setUser] = useState(null);
  const [userExists, setUserExists] = useState(false);
  // Host home location (lives in userLocations/{uid}, shared with the member side).
  const [homeLoc, setHomeLoc] = useState(null);
  const [citySearch, setCitySearch] = useState("");
  const [cityResults, setCityResults] = useState([]);
  const [searchingCity, setSearchingCity] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);


  useEffect(() => {
    const fetchVendor = async () => {
      try {
        const vendorData = await Api.getDocument("vendors", vendorID).catch(() => null);
        if (vendorData) {
          setVendor({ id: vendorID, ...vendorData });
          const locationData = await Api.getDocument("userLocations", vendorID).catch(() => null);
          if (locationData?.home) setHomeLoc(locationData.home);
          const userData = await Api.getDocument("users", vendorID).catch(() => null);
          if (userData) {
            setUser({
              name: userData.name || "",
              photoURL: userData.photoURL || "",
              phone: userData.phone || "",
              email: userData.email || "",
              bio: userData.bio || "",
            });
            setUserExists(true);
          }
        } else {
          showToast("Host not found", "error");
          navigate("/hosts");
        }
      } catch (error) {
        console.error("Error fetching vendor:", error);
        showToast("Error loading host", "error");
      } finally {
        setLoading(false);
      }
    };
    if (vendorID) {
      fetchVendor();
    }
  }, [vendorID, navigate]);


  const handleDeleteVendor = async () => {
    if (!window.confirm(`Delete the vendor profile for "${user?.name || vendorID}"? The user account will be kept. This cannot be undone.`)) return;
    setDeletingVendor(true);
    try {
      await callFunction("adminDeleteVendorProfile", { vendorID });
      showToast("Vendor profile deleted");
      navigate("/hosts");
    } catch (error) {
      console.error("Error deleting vendor:", error);
      showToast(error.message || "Failed to delete vendor profile", "error");
      setDeletingVendor(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Delete the user account for "${user?.name || vendorID}"? The vendor profile will be kept. This cannot be undone.`)) return;
    setDeletingUser(true);
    try {
      await callFunction("adminDeleteUserAccount", { userID: vendorID });
      showToast("User account deleted");
      setUser(null);
      setUserExists(false);
    } catch (error) {
      console.error("Error deleting user:", error);
      showToast(error.message || "Failed to delete user account", "error");
    } finally {
      setDeletingUser(false);
    }
  };

  const handlePurgeHost = async () => {
    const label = user?.name || vendorID;
    if (!window.confirm(
      `PERMANENTLY delete EVERYTHING for "${label}"?\n\n` +
      "This removes the user account, the vendor profile, their event/match " +
      "references, stored photos, and the login. This cannot be undone."
    )) return;
    const typed = window.prompt(`To confirm, type the host's name exactly:\n\n${label}`);
    if (typed == null) return; // cancelled
    if (typed.trim() !== label) {
      showToast("Name did not match — purge cancelled", "error");
      return;
    }
    setPurging(true);
    try {
      await callFunction("adminPurgeHost", { uid: vendorID });
      showToast("Host fully deleted");
      navigate("/hosts");
    } catch (error) {
      console.error("Error purging host:", error);
      showToast(error.message || "Failed to delete host", "error");
      setPurging(false);
    }
  };

  const handleCitySearch = async () => {
    const query = citySearch.trim();
    if (query.length < 2) return;
    setSearchingCity(true);
    try {
      const res = await callFunction("geocodeCity", { query });
      setCityResults(res?.results || []);
    } catch (error) {
      console.error("geocodeCity failed:", error);
      showToast(error.message || "City search failed", "error");
    } finally {
      setSearchingCity(false);
    }
  };

  const handlePickCity = async (place) => {
    setSavingLocation(true);
    try {
      const location = {
        city: place.city || place.label || "",
        state: place.state || "Unknown",
        country: place.country || "Unknown",
        lat: place.lat,
        lng: place.lng,
      };
      const res = await callFunction("adminSetHostHomeLocation", { uid: vendorID, location });
      if (res?.location?.home) setHomeLoc(res.location.home);
      setCityResults([]);
      setCitySearch("");
      showToast("Host home location updated");
    } catch (error) {
      console.error("adminSetHostHomeLocation failed:", error);
      showToast(error.message || "Failed to set location", "error");
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // One batch so vendors + users commit atomically. Two sequential
      // updateDocs could leave a half-updated host when the second write
      // failed after the first had already landed.
      const batch = writeBatch(db);
      batch.update(doc(db, "vendors", vendorID), {
        meetupMemberID: vendor.meetupMemberID || "",
        bio: (vendor.bio || "").trim(),
        isSuperAdmin: vendor.isSuperAdmin || false,
      });
      if (userExists && user) {
        batch.update(doc(db, "users", vendorID), {
          name: user.name || "",
          photoURL: user.photoURL || "",
          phone: user.phone || "",
          email: user.email || "",
          bio: user.bio || "",
        });
      }
      await batch.commit();
      showToast("Host updated successfully");
      navigate("/hosts");
    } catch (error) {
      console.error("Error updating vendor:", error);
      showToast("Failed to update host", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-md shadow-md mt-6">
        <div className="flex items-center justify-between border-b border-gray-900/10 dark:border-white/10 pb-4">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Edit Host</h1>
          <div className="flex items-center gap-x-3">
            <button
              type="button"
              onClick={() => navigate("/hosts")}
              className="rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || deletingVendor || deletingUser || purging}
              className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {userExists && user && (
          <div className="mt-6 border-b border-gray-900/10 dark:border-white/10 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-x-4">
                {user.photoURL ? (
                  <img
                    src={variantURL(user.photoURL, 200)}
                    onError={fallbackToOriginal(user.photoURL)}
                    alt={user.name}
                    className="h-16 w-16 rounded-full object-cover ring-2 ring-gray-200 dark:ring-gray-700"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-700 ring-2 ring-gray-200 dark:ring-gray-700" />
                )}
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">User Account</h2>
              </div>
              {globalState.isSuperAdmin && (
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  disabled={deletingUser || deletingVendor || saving || purging}
                  className="rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-300 shadow-sm ring-1 ring-inset ring-red-300 dark:ring-red-800 hover:bg-red-50 dark:hover:bg-red-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingUser ? "Deleting..." : "Delete User"}
                </button>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
              <div className="sm:col-span-4">
                <Input
                  label="Name"
                  type="text"
                  value={user.name || ""}
                  onChange={(e) => setUser({ ...user, name: e.target.value })}
                  required={false}
                />
              </div>

              <div className="sm:col-span-4">
                <Input
                  label="Photo URL"
                  type="text"
                  value={user.photoURL || ""}
                  onChange={(e) => setUser({ ...user, photoURL: e.target.value })}
                  required={false}
                />
              </div>

              <div className="sm:col-span-4">
                <Input
                  label="Email"
                  type="email"
                  value={user.email || ""}
                  onChange={(e) => setUser({ ...user, email: e.target.value })}
                  required={false}
                />
              </div>

              <div className="sm:col-span-4">
                <Input
                  label="Phone"
                  type="text"
                  value={user.phone || ""}
                  onChange={(e) => setUser({ ...user, phone: e.target.value })}
                  required={false}
                />
              </div>

              <div className="sm:col-span-6">
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Bio</label>
                <textarea
                  rows={3}
                  value={user.bio || ""}
                  onChange={(e) => setUser({ ...user, bio: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Vendor Settings</h2>
            {globalState.isSuperAdmin && (
              <button
                type="button"
                onClick={handleDeleteVendor}
                disabled={deletingVendor || deletingUser || saving || purging}
                className="rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-300 shadow-sm ring-1 ring-inset ring-red-300 dark:ring-red-800 hover:bg-red-50 dark:hover:bg-red-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingVendor ? "Deleting..." : "Delete Vendor"}
              </button>
            )}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <Input
                label="Vendor ID"
                type="text"
                value={vendor.vendorID || ""}
                onChange={() => {}}
                required={false}
                className="opacity-60"
              />
            </div>

            <div className="sm:col-span-4">
              <Input
                label="Meetup Member ID"
                type="text"
                value={vendor.meetupMemberID || ""}
                onChange={(e) => setVendor({ ...vendor, meetupMemberID: e.target.value })}
                required={false}
              />
            </div>

            <div className="sm:col-span-6">
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Host Bio</label>
              <textarea
                rows={4}
                value={vendor.bio || ""}
                onChange={(e) => setVendor({ ...vendor, bio: e.target.value })}
                placeholder="A short intro shown to attendees on this host's event pages."
                className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 resize-y"
              />
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Shown publicly on event pages alongside the host's name and photo.
              </p>
            </div>

            <div className="sm:col-span-4">
              <label className="flex items-center gap-x-3">
                <input
                  type="checkbox"
                  checked={vendor.isSuperAdmin || false}
                  onChange={(e) => setVendor({ ...vendor, isSuperAdmin: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Super Admin</span>
              </label>
            </div>
          </div>
        </div>


        <div className="mt-6 border-t border-gray-900/10 dark:border-white/10 pt-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Home Location</h2>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            The host's home metro. Determines which region's events and ideas they see, and which
            region new ideas are generated for. Not shown publicly.
          </p>

          <div className="mt-4 flex items-center gap-x-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">Current:</span>
            {homeLoc ? (
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {[homeLoc.city, homeLoc.state, homeLoc.country].filter(Boolean).join(", ")}
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                  ({homeLoc.lat?.toFixed?.(3)}, {homeLoc.lng?.toFixed?.(3)} · {homeLoc.source})
                </span>
              </span>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500">Not set</span>
            )}
          </div>

          <div className="mt-4 flex items-end gap-x-3">
            <div className="flex-1 max-w-md">
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Search a city</label>
              <input
                type="text"
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCitySearch(); } }}
                placeholder="e.g. New York, Austin, London"
                className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
              />
            </div>
            <button
              type="button"
              onClick={handleCitySearch}
              disabled={searchingCity || citySearch.trim().length < 2}
              className="rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {searchingCity ? "Searching..." : "Search"}
            </button>
          </div>

          {cityResults.length > 0 && (
            <ul className="mt-3 max-w-md divide-y divide-gray-100 dark:divide-gray-800 rounded-md ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
              {cityResults.map((place, i) => (
                <li key={`${place.label}-${i}`}>
                  <button
                    type="button"
                    onClick={() => handlePickCity(place)}
                    disabled={savingLocation}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    <span>{place.label}</span>
                    <span className="text-xs text-primary">{savingLocation ? "Saving..." : "Set as home"}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {vendor.stripeAccountId && (
          <div className="mt-6 border-t border-gray-900/10 dark:border-white/10 pt-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Stripe Info</h2>
            <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Account ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{vendor.stripeAccountId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{vendor.stripeAccountStatus || "N/A"}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Onboarding Complete</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{vendor.stripeOnboardingComplete ? "Yes" : "No"}</dd>
              </div>
            </dl>
          </div>
        )}

        {globalState.isSuperAdmin && (
          <div className="mt-6 border-t border-red-200 dark:border-red-800 pt-6">
            <h2 className="text-base font-semibold text-red-700 dark:text-red-300">Danger Zone</h2>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Permanently remove this host and everything tied to them — user
              account, vendor profile, event/match references, stored photos, and
              the login. This cannot be undone.
            </p>
            <div className="mt-4 flex items-center justify-end">
              <button
                type="button"
                onClick={handlePurgeHost}
                disabled={purging || deletingVendor || deletingUser || saving}
                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purging ? "Deleting..." : "Delete Everything"}
              </button>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
